import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, User, Eye, EyeOff, Crown, Sparkles } from 'lucide-react';
import { managerAPI } from '../utils/api';

const ManagerLogin = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await managerAPI.login(formData.identifier, formData.password);
      if (response.data.success) {
        const { token, manager } = response.data;
        sessionStorage.setItem('managerToken', token.accessToken);
        if (manager) {
          sessionStorage.setItem('managerUser', JSON.stringify(manager));
        }
        navigate('/manager/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your Manager credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center p-4 font-['Outfit'] overflow-hidden relative">
      {/* Dynamic Ambient Background Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-indigo-600/20 blur-[140px] rounded-full animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-purple-600/20 blur-[140px] rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[35%] h-[35%] bg-violet-600/15 blur-[160px] rounded-full"></div>

      <div className="w-full max-w-[460px] relative z-10">
        {/* Glass Container */}
        <div className="bg-[#0b0f19]/80 backdrop-blur-2xl border border-indigo-500/20 rounded-[40px] p-8 md:p-12 shadow-2xl shadow-indigo-950/50 overflow-hidden relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>

          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-violet-500 p-[2px] mb-5 group transition-transform hover:scale-105 duration-500 shadow-xl shadow-indigo-500/25">
               <div className="w-full h-full bg-[#0a0c16] rounded-[22px] flex items-center justify-center">
                  <Crown size={38} className="text-indigo-400 group-hover:rotate-6 transition-transform" />
               </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-2">
              XOXO <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 to-violet-400">MANAGER</span>
            </h1>
            <p className="text-slate-400 text-sm font-medium">Executive Hierarchy & BD Team Portal</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-semibold text-center animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Manager Identifier</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                  <User size={20} />
                </div>
                <input
                  type="text"
                  name="identifier"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:bg-white/10 transition-all font-medium text-sm"
                  placeholder="Manager ID (e.g. 10001), Email or Phone"
                  value={formData.identifier}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Password</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                  <Lock size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-12 text-white placeholder:text-slate-600 outline-none focus:border-indigo-500/60 focus:bg-white/10 transition-all font-medium text-sm"
                  placeholder="Enter your Manager password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 mt-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-bold text-sm tracking-wide shadow-xl shadow-indigo-600/30 flex items-center justify-center gap-2 group transition-all duration-300 disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Sign In as Manager</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default ManagerLogin;
